import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Palette, Headphones, BookOpen, Gift, ArrowUpRight, Download, ShoppingBag } from "lucide-react";
import { eduMaterials, freeResources } from "@/data/edu";
import { useCart } from "@/hooks/useCart";
import { toast } from "sonner";

const iconMap = {
  palette: Palette,
  headphones: Headphones,
  book: BookOpen,
  gift: Gift,
};

export default function EduSection() {
  const { addItem } = useCart();

  return (
    <section
      id="pomoce"
      className="relative py-28 md:py-40 overflow-hidden bg-gradient-to-b from-[#FFF7ED]/40 via-[#F4FAFF] to-[#F4FAFF]"
      data-testid="edu-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <div>
            <span className="chip" data-testid="edu-eyebrow">
              <span className="dot" /> Pomoce edukacyjne
            </span>
            <h2 className="font-display font-semibold text-[#051937] text-4xl sm:text-5xl lg:text-6xl mt-6 tracking-tight leading-[1.02] max-w-3xl">
              Kolorowanki, karty pracy, audiobook —{" "}
              <em className="not-italic text-[#60A5FA]">bajki, które robią więcej.</em>
            </h2>
          </div>
          <p className="text-[#0b2a55]/75 text-lg max-w-sm">
            Zaprojektowane po to, żeby po przeczytaniu bajki dziecko miało co robić z Tonkiem i Duszkiem dalej — same, z rodzeństwem, w podróży.
          </p>
        </motion.div>

        <motion.ul
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {eduMaterials.map((m) => {
            const Icon = iconMap[m.icon] || BookOpen;
            return (
              <motion.li
                key={m.slug}
                variants={{
                  hidden: { opacity: 0, y: 40 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
                }}
                whileHover={{ y: -6, rotate: -0.5 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="rounded-[2rem] p-6 md:p-7 relative overflow-hidden group"
                style={{ background: `linear-gradient(160deg, ${m.color}55 0%, ${m.color}22 100%)`, border: `1px solid ${m.accent}44` }}
                data-testid={`edu-card-${m.slug}`}
              >
                <div
                  className="w-14 h-14 rounded-2xl grid place-items-center text-white mb-5"
                  style={{ background: m.accent }}
                >
                  <Icon size={26} strokeWidth={2.2} />
                </div>
                <h3 className="font-display font-semibold text-[#051937] text-2xl leading-tight">
                  {m.title}
                </h3>
                <p className="mt-2 text-xs uppercase tracking-[0.14em] font-bold text-[#0b2a55]/60">
                  {m.subtitle}
                </p>
                <p className="mt-4 text-sm text-[#0b2a55]/80 leading-relaxed">{m.tagline}</p>
                <div className="mt-6 flex items-center justify-between">
                  <span className="font-display font-semibold text-[#051937] text-xl">
                    {m.price_pln} zł
                  </span>
                  <button
                    onClick={() => {
                      addItem(m.lookup_key);
                      toast.success(`${m.title} — w koszyku ✨`);
                    }}
                    data-testid={`add-edu-${m.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#051937] hover:bg-[#0b2a55] text-white px-4 py-2 text-xs font-semibold"
                  >
                    <ShoppingBag size={14} /> Kup
                  </button>
                </div>

                {/* Floating deco */}
                <span
                  aria-hidden="true"
                  className="absolute -bottom-8 -right-6 w-32 h-32 rounded-full opacity-40 group-hover:opacity-70 transition-opacity"
                  style={{ background: m.accent, filter: "blur(30px)" }}
                />
              </motion.li>
            );
          })}
        </motion.ul>

        {/* Free downloads */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9 }}
          className="mt-20 md:mt-28 rounded-[2rem] md:rounded-[3rem] bg-[#E0F2FE]/60 border border-[#BAE6FD] p-8 md:p-12"
          data-testid="free-resources"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <span className="chip !bg-white !text-[#051937]">
                <span className="dot" /> Za darmo
              </span>
              <h3 className="font-display font-semibold text-[#051937] text-2xl md:text-3xl mt-4">
                Zaraz do pobrania — bez rejestracji.
              </h3>
            </div>
            <Link
              to="/kolorowanki"
              className="text-sm font-semibold text-[#051937] animated-underline"
              data-testid="free-more"
            >
              Zobacz więcej darmowych <ArrowUpRight className="inline ml-1" size={14} />
            </Link>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {freeResources.map((r) => (
              <li key={r.slug} data-testid={`free-${r.slug}`}>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group block rounded-[1.5rem] bg-white border border-white p-5 hover:-translate-y-1 transition-transform"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-[#FDE68A] grid place-items-center text-[#051937]">
                      <Download size={18} />
                    </span>
                    <span className="text-xs uppercase tracking-[0.14em] text-[#0b2a55]/60 font-bold">
                      {r.kind}
                    </span>
                  </div>
                  <h4 className="mt-3 font-display font-semibold text-[#051937] text-lg leading-tight">
                    {r.title}
                  </h4>
                  <p className="mt-2 text-sm text-[#0b2a55]/75">{r.description}</p>
                </a>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mt-14 flex flex-wrap items-center justify-center gap-4"
        >
          <Link
            to="/quiz"
            data-testid="edu-quiz-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#FDE68A] hover:bg-[#FCD34D] text-[#051937] px-6 py-3 font-semibold shadow-[0_20px_50px_-20px_rgba(245,158,11,0.6)]"
          >
            Rozwiąż quiz z Duszkiem <ArrowUpRight size={16} />
          </Link>
          <Link
            to="/sklep"
            data-testid="edu-shop-cta"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[#BAE6FD] text-[#051937] px-6 py-3 font-semibold"
          >
            Wszystkie pomoce w sklepie <ArrowUpRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
